import { IStyle, mergeStyleSets } from "@fluentui/merge-styles";
import CheckIcon from "@mui/icons-material/Check";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { DATA_GRID_CONFIG } from "../../SharedComponents/Constants/CustomCustomerList.constants";
import { CustomDataGrid } from "../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { handleToggleDataGridRowSelection } from "../../Utils/js-common";
import { IPartnerProfileProps } from "../../interfaces/PartnerView.interfaces";
import { renderHeaderWithTooltip } from "../PartnerSearch/PartnerSearch.common";
import { tooltipTexts } from "../PartnerSearch/PartnerSearch.constants";

const styles = mergeStyleSets({
  featureTable: {
    height: 700,
    width: "100%"
  } as IStyle
});

export default function PartnerProfileTable(props: IPartnerProfileProps) {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [paginationOptions, setPaginationOptions] = useState(DATA_GRID_CONFIG.pagination);
  const handleSelection = handleToggleDataGridRowSelection(selectionModel, setSelectionModel);
  useEffect(() => {
    setTimeout(() => {
      const elements = document.getElementsByClassName("MuiDataGrid-virtualScroller");
      Array.from(elements).forEach(function (element) {
        element.setAttribute("tabIndex", "0");
      });
    }, 1000);
  }, []);

  const columns: GridColDef[] = [
    {
      headerName: "PartnerID",
      field: "mpnId",
      width: 128,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.partnerID)
    },
    {
      headerName: "PartnerName",
      field: "mpnName",
      width: 250,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.partnerName)
    },
    {
      headerName: "PartnerOneID",
      field: "partnerOneID",
      width: 155,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.partnerOneId)
    },
    {
      headerName: "PartnerOneName",
      field: "partnerOneName",
      width: 178,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.partnerOneName)
    },
    {
      headerName: "Partner Country",
      field: "partnerCountry",
      width: 170,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.partnerCountry)
    },
    {
      headerName: "IsCPOR",
      field: "isCPOR",
      width: 115,
      sortable: true,
      hideSortIcons: true,
      type: "boolean",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.isCPOR),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {!row.isCPOR ? (
            <></>
          ) : (
            <CheckIcon
              titleAccess="true"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "IsCSP",
      field: "isCSP",
      width: 105,
      sortable: true,
      hideSortIcons: true,
      type: "boolean",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.isCSP),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {!row.isCSP ? (
            <></>
          ) : (
            <CheckIcon
              titleAccess="true"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "IsPAL",
      field: "isPAL",
      width: 105,
      sortable: true,
      hideSortIcons: true,
      type: "boolean",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.isPAL),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {!row.isPAL ? (
            <></>
          ) : (
            <CheckIcon
              titleAccess="true"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "CPOR Workloads Count",
      field: "cporWorkloadsCount",
      width: 220,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => (renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.cporTenantsCount))
    },
    {
      headerName: "CSP Subscription SKU Count",
      field: "cspSubscriptionSKUCount",
      width: 250,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.cspSubscriptionCount)
    },
    {
      headerName: "PAL Services Count",
      field: "palServicesCount",
      width: 190,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.palServicesCount)
    },
    {
      headerName: "CPOR Tenant Count",
      field: "cporTenantCount",
      width: 195,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.cporTenantsCount)
    },
    {
      headerName: "CSP Tenant Count",
      field: "cspTenantCount",
      width: 185,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.cspTenantsCount)
    },
    {
      headerName: "PAL Tenant Count",
      field: "palTenantCount",
      minWidth: 180,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.palTenantsCount),
      flex: 1
    }
  ];

  return (
    <div className={styles.featureTable}>
      <CustomDataGrid
        enableUrlParams
        rows={{ values: props.tableData ?? [], count: props.tableData?.length ?? 0 }}
        columns={columns}
        getRowId={(row) => row.mpnId}
        onRowSelectionModelChange={handleSelection}
        rowSelectionModel={selectionModel}
        paginationOptions={paginationOptions}
        setPaginationOptions={setPaginationOptions}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 }
          }
        }}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            pointerEvents: "none" // disables all pointer events for header
          },
          "& .MuiDataGrid-columnHeader .MuiSvgIcon-root": {
            pointerEvents: "auto", // re-enables pointer events for icons
            cursor: "pointer" // shows pointer cursor on hover
          },
          "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
            pointerEvents: "auto", // re-enables pointer events for menu icon
            cursor: "pointer" // shows pointer cursor on hover
          }
        }}
        kustoLink={props.kustoLink}
      />
    </div>
  );
}
